import { StateCreator } from 'zustand';
import { FeatureCollection } from 'geojson';
import { RedlineTool } from './types';

export interface RedlineSlice {
  availableRedlineTools: string[];
  activeRedlineTool: RedlineTool;
  redlineData: FeatureCollection | null;
  targetAssetId: string | null;
  activeSketchFeatureId: string | null;

  setAvailableRedlineTools: (tools: string[]) => void;
  setRedlineTool: (tool: RedlineTool) => void;
  updateRedlineData: (data: FeatureCollection) => void;
  deleteSketchFeature: (id: string) => void;
  clearRedlineData: () => void;
  resetFieldCorrectionState: () => void;
  setTargetAssetId: (id: string | null) => void;
  setActiveSketchFeatureId: (id: string | null) => void;
  updateSketchProperties: (id: string, attrs: Record<string, any>) => void;
}

export const createRedlineSlice: StateCreator<RedlineSlice> = (set, get) => ({
  availableRedlineTools: [],
  activeRedlineTool: 'NONE',
  redlineData: null,
  targetAssetId: null,
  activeSketchFeatureId: null,

  setAvailableRedlineTools: (tools) => set({ availableRedlineTools: tools }),
  setRedlineTool: (tool) => set({ activeRedlineTool: tool }),
  updateRedlineData: (data) => set({ redlineData: data }),
  setTargetAssetId: (id) => set({ targetAssetId: id }),
  setActiveSketchFeatureId: (id) => set({ activeSketchFeatureId: id }),

  deleteSketchFeature: (idToDelete) => {
    const { redlineData, activeSketchFeatureId } = get();
    if (!redlineData) return;

    const remaining = redlineData.features.filter(
      (f: any) => f.id !== idToDelete,
    );

    const cleaned = remaining.map((f: any) => {
      const { upstream, downstream } = f.properties?.connections ?? {};
      if (!upstream && !downstream) return f;

      const cleanUp = upstream?.filter((id: string) => id !== idToDelete) ?? [];
      const cleanDown =
        downstream?.filter((id: string) => id !== idToDelete) ?? [];

      if (
        cleanUp.length === upstream?.length &&
        cleanDown.length === downstream?.length
      ) {
        return f;
      }
      return {
        ...f,
        properties: {
          ...f.properties,
          connections: { upstream: cleanUp, downstream: cleanDown },
        },
      };
    });

    set({
      redlineData: { ...redlineData, features: cleaned },
      activeSketchFeatureId:
        activeSketchFeatureId === idToDelete ? null : activeSketchFeatureId,
    });
  },

  clearRedlineData: () =>
    set({
      redlineData: null,
      activeRedlineTool: 'NONE',
      //   availableRedlineTools: [],
      activeSketchFeatureId: null,
    }),

  resetFieldCorrectionState: () =>
    set({
      interactionMode: 'view',
      activeRedlineTool: 'NONE',
      activeSketchFeatureId: null,
      selectionCandidates: null,
      redlineData: null,
    } as any),

  updateSketchProperties: (id, newAttrs) =>
    set((state) => {
      if (!state.redlineData) return state;

      const updatedFeatures = state.redlineData.features.map((f: any) => {
        const fId = f.id ?? f.properties?.id;
        if (fId !== id) return f;
        return {
          ...f,
          properties: { ...f.properties, ...newAttrs },
        };
      });

      return {
        redlineData: { ...state.redlineData, features: updatedFeatures },
      };
    }),
});
